import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { Config } from '@react-awesome-query-builder/mui';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  useLookupGeometries,
  useLookupLists,
  useLookupMailing,
} from '@/criteriaLookups';
import { useLookupGroups } from '@components/GroupsFetchAutocomplete';
import { useLookupTags } from '@components/TagsFetchAutocomplete';
import { usePathwaySteps } from '@hooks/usePathways';
import DeletionInterceptionButton from '@pages/SearchTemplatePage/deletionConfirmation/DeletionInterceptionButton';
import {
  ConfirmContextValue,
  useConfirmDeleteRuleOrGroup,
} from '@/pages/SearchTemplatePage/deletionConfirmation/ConfirmDeleteRuleOrGroupContext';
import AccessibilityMuiButton from '@pages/AdvancedSearchListCreatorPage/customWidgets/AccessibilityMuiButton';
import AccessibilityMuiSwitch from '@pages/AdvancedSearchListCreatorPage/customWidgets/AccessibilityMuiSwitch';
import AccessibilityMuiValueSources from '@pages/AdvancedSearchListCreatorPage/customWidgets/AccessibilityMuiValueSources';
import { useLookupBranches, useLookupElections } from '@pages/AdvancedSearchListCreatorPage/hooks';
import { buildQueryBuilderFields } from './fields';
import {
  buildQueryBuilderOperators,
  buildQueryBuilderTypes,
  buildQueryBuilderWidgets,
  initialConfig,
} from './qbTypesAndWidgets';
import { UseQueryBuilderConfigOptions } from './types';

type RenderConfirmProps = {
  confirmFn: ConfirmContextValue;
  onOk: () => void;
};

/** RAQB only calls the confirm function if the rule or group being deleted is
 * non-empty and if the relevant options object is set, but not what it
 * contains, so we just use an empty object since we don't need to use the values
 * in it. The titles, description etc are set by the provider based on the
 * pending delete target and placeholder presence in the tree. */
const DUMMY_CONFIRM_OPTIONS = {};

export default function useQueryBuilderConfig({
  isOnTemplatePage = false,
}: UseQueryBuilderConfigOptions = {}): Config {
  const domainCode = useAtomValue(domainCodeAtom);
  const lookupGeometries = useLookupGeometries();
  const lookupTags = useLookupTags();
  const lookupGroups = useLookupGroups();
  const lookupLists = useLookupLists();
  const { data: pathwaySteps } = usePathwaySteps();
  const lookupBranches = useLookupBranches();
  const lookupElections = useLookupElections();
  const lookupMailings = useLookupMailing();

  return useMemo(() => {
    const fieldDeps = {
      isOnTemplatePage,
      domainCode,
      lookupGeometries,
      lookupTags,
      lookupGroups,
      lookupLists,
      lookupBranches,
      lookupElections,
      lookupMailings,
      pathwaySteps,
    };

    return {
      ...initialConfig,
      ctx: {
        ...initialConfig.ctx,
        W: {
          ...initialConfig.ctx.W,
          MuiButton: isOnTemplatePage ? DeletionInterceptionButton : AccessibilityMuiButton,
          MuiValueSources: AccessibilityMuiValueSources,
          MuiSwitch: AccessibilityMuiSwitch,
        },
      },
      settings: {
        ...initialConfig.settings,
        clearValueOnChangeField: true,
        fieldPlaceholder: 'Select filter',
        removeRuleConfirmOptions: isOnTemplatePage ? DUMMY_CONFIRM_OPTIONS : undefined,
        removeGroupConfirmOptions: isOnTemplatePage ? DUMMY_CONFIRM_OPTIONS : undefined,
        renderConfirm: isOnTemplatePage
          ? async ({ confirmFn, onOk }: RenderConfirmProps) => {
              const confirmed = await confirmFn();
              if (confirmed) {
                onOk();
              }
            }
          : undefined,
        useConfirm: isOnTemplatePage ? useConfirmDeleteRuleOrGroup : undefined,
      },
      operators: buildQueryBuilderOperators(initialConfig),
      types: buildQueryBuilderTypes(initialConfig),
      widgets: buildQueryBuilderWidgets(initialConfig),
      fields: buildQueryBuilderFields(fieldDeps) as Config['fields'],
    };
  }, [
    lookupElections,
    domainCode,
    lookupGroups,
    lookupLists,
    lookupGeometries,
    lookupMailings,
    lookupBranches,
    lookupTags,
    pathwaySteps,
    isOnTemplatePage,
  ]);
}
